import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useRef } from 'react';
import { track } from '@/lib/analytics';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import {
  FiSearch,
  FiDollarSign,
  FiCalendar,
  FiHome,
  FiCheckCircle,
} from 'react-icons/fi';

const TOTAL_STEPS = 11;

const BUILDING_STEPS = [
  { label: 'Analyzing your needs...', icon: FiSearch },
  { label: 'Setting up expense tracking...', icon: FiDollarSign },
  { label: 'Preparing your calendar...', icon: FiCalendar },
  { label: 'Configuring your household...', icon: FiHome },
];

const STEP_DURATION = 800; // ms per step
const TOTAL_DURATION = BUILDING_STEPS.length * STEP_DURATION + 500;

function BuildingPlanPage() {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const animRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    track('Building Plan Screen');
  }, []);

  // Animate progress
  useEffect(() => {
    const animDuration = TOTAL_DURATION - 500;
    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / animDuration, 1);
      setProgressPercent(Math.round(progress * 100));
      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  // Step through building steps
  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    BUILDING_STEPS.forEach((_, index) => {
      const timeout = setTimeout(() => {
        setCurrentStepIndex(index);
      }, index * STEP_DURATION);
      timeouts.push(timeout);
    });

    // Mark complete
    const completeTimeout = setTimeout(() => {
      setIsComplete(true);
      track('Building Plan Completed');

      // Navigate after a brief pause
      setTimeout(() => {
        navigate({ to: '/app/expenses' });
      }, 1000);
    }, TOTAL_DURATION - 500);
    timeouts.push(completeTimeout);

    return () => timeouts.forEach(clearTimeout);
  }, [navigate]);

  const CurrentIcon = BUILDING_STEPS[currentStepIndex].icon;

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <OnboardingProgress currentStep={11} totalSteps={TOTAL_STEPS} />
      <div className='flex-1 flex flex-col items-center justify-center px-6 max-w-lg mx-auto w-full'>
        {!isComplete ? (
          <>
            {/* Progress Circle */}
            <div className='w-40 h-40 rounded-full bg-primary/[0.08] flex items-center justify-center mb-8 relative overflow-hidden'>
              <div
                className='absolute bottom-0 left-0 right-0 bg-primary/[0.15] transition-all duration-300'
                style={{ height: `${progressPercent}%` }}
              />
              <span
                className='text-[42px] font-extrabold text-primary relative z-10'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {progressPercent}%
              </span>
            </div>

            {/* Current Step */}
            <div className='flex items-center gap-3'>
              <CurrentIcon className='w-6 h-6 text-primary' />
              <span
                className='text-lg text-gray-700'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                {BUILDING_STEPS[currentStepIndex].label}
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Complete State */}
            <div className='mb-6 animate-bounce'>
              <FiCheckCircle className='w-20 h-20 text-green-600' />
            </div>
            <h1
              className='text-[28px] font-extrabold text-gray-900 text-center mb-2'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Your Harbor is ready!
            </h1>
            <p
              className='text-base text-gray-500 text-center'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              Let's start co-parenting peacefully.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/building-plan')({
  component: BuildingPlanPage,
});
